import { sessionListSearchValueAtom } from '@/atoms/sideNavAtoms';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { isEmpty } from 'lodash-es';
import { type MouseEvent, useCallback, useMemo } from 'react';
import {
  isClinicianFilterApplicableForScope,
  isPatientProfileFilterApplicableForScope,
  isSessionsFiltersOpenAtom,
  isUrgentOnlyFilterAtom,
  selectedClinicianIdsFilterAtom,
  selectedDateRangeFilterAtom,
  selectedPatientProfilesFilterAtom,
  selectedStatusesFilterAtom,
  sessionListScopeAtom,
  sessionsScrollToTopTriggerAtom,
} from '../atoms';

export const useSessionsFilters = () => {
  const [isOpen, setIsOpen] = useAtom(isSessionsFiltersOpenAtom);

  const [selectedDateRangeFilter, setSelectedDateRangeFilter] = useAtom(
    selectedDateRangeFilterAtom
  );
  const [selectedStatusFilter, setSelectedStatusFilter] = useAtom(
    selectedStatusesFilterAtom
  );
  const [isUrgentOnlyFilter, setIsUrgentOnlyFilter] = useAtom(isUrgentOnlyFilterAtom);
  const [selectedPatientProfilesFilter, setSelectedPatientProfilesFilter] = useAtom(
    selectedPatientProfilesFilterAtom
  );
  const [selectedClinicianIds, setSelectedClinicianIds] = useAtom(
    selectedClinicianIdsFilterAtom
  );
  const scope = useAtomValue(sessionListScopeAtom);
  const setSessionListSearchValue = useSetAtom(sessionListSearchValueAtom);
  const setScrollToTopTrigger = useSetAtom(sessionsScrollToTopTriggerAtom);

  const sessionListSearchValue = useAtomValue(sessionListSearchValueAtom);

  const isFilterApplied = useMemo(() => {
    const clinicianFilterApplied =
      isClinicianFilterApplicableForScope(scope) && !isEmpty(selectedClinicianIds);
    const patientProfileFilterApplied =
      isPatientProfileFilterApplicableForScope(scope) &&
      !isEmpty(selectedPatientProfilesFilter);
    return (
      (selectedDateRangeFilter.from && selectedDateRangeFilter.to) ||
      !isEmpty(selectedStatusFilter) ||
      isUrgentOnlyFilter ||
      patientProfileFilterApplied ||
      clinicianFilterApplied ||
      !isEmpty(sessionListSearchValue)
    );
  }, [
    selectedDateRangeFilter.from,
    selectedDateRangeFilter.to,
    selectedStatusFilter,
    isUrgentOnlyFilter,
    selectedPatientProfilesFilter,
    selectedClinicianIds,
    scope,
    sessionListSearchValue,
  ]);

  const handleClearFilters = useCallback(
    (e?: MouseEvent<HTMLElement>) => {
      e?.preventDefault();
      setSelectedDateRangeFilter({ from: undefined, to: undefined });
      setSelectedStatusFilter([]);
      setIsUrgentOnlyFilter(false);
      // Only clear the patient selection where the filter is actually shown. On
      // scopes that hide it, the (global) selection is preserved — persist-and-
      // ignore — so returning to My sessions restores it. This mirrors the
      // scope-keyed clinician clear, which only ever touches the current scope.
      if (isPatientProfileFilterApplicableForScope(scope)) {
        setSelectedPatientProfilesFilter([]);
      }
      setSelectedClinicianIds([]);
      setSessionListSearchValue('');
      setScrollToTopTrigger((prev) => prev + 1);
    },
    [
      scope,
      setSelectedDateRangeFilter,
      setSelectedStatusFilter,
      setIsUrgentOnlyFilter,
      setSelectedPatientProfilesFilter,
      setSelectedClinicianIds,
      setSessionListSearchValue,
      setScrollToTopTrigger,
    ]
  );

  return {
    isOpen,
    setIsOpen,
    isFilterApplied,
    handleClearFilters,
  };
};
